import React, { useState } from 'react';
import { Form, Modal, Row, Col, Alert, Button } from 'react-bootstrap-5';
import PropTypes from 'prop-types';
import { post } from 'helpers/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ShareLinkEmailInput from './inputs/share_link_email_input';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';
import { isValidEmail } from './helpers/is_valid_email';
import Snackbar from 'shared/snackbar';

export default function ShareLinkByEmailModal({
  additionalInviteProps = {},
  description,
  email,
  fromEmails,
  hideModal,
  leadId,
  propertyId,
  show,
  title
}) {
  const [newInvite, setNewInvite] = useState(getDefaultNewInvite(email, fromEmails));
  const [error, setError] = useState('');
  const [errorDetail, setErrorDetail] = useState('');
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [isSent, setIsSent] = useState(false);

  const validEmail = isValidEmail(newInvite.email);
  const updateField = (field, value) => setNewInvite({ ...newInvite, [field]: value });

  const save = (e) => {
    e.preventDefault();
    if (isSendingInvite) {
      return;
    }
    if (!validEmail) return;

    setIsSendingInvite(true);
    setError('');
    setErrorDetail('');

    post(`/docs/document_share_links`, {
      propertyId,
      invite: {
        email: newInvite.email,
        from_user_id: newInvite.from,
        message: newInvite.message,
        lead_id: leadId,
        ...additionalInviteProps
      }
    }).then(() => {
      closeModal();

      setIsSent(true);
    }).catch((error) => {
      setError('We were unable to send the invite, please try again or contact support');
      if (error.response?.status === 422) {
        setErrorDetail(error.response.data?.error);
      }
      setIsSendingInvite(false);
    });
  };

  const closeModal = () => {
    hideModal();
    setError('');
    setErrorDetail('');
    setIsSendingInvite(false);
    setNewInvite(getDefaultNewInvite(email, fromEmails));
  };



  return (
    <React.Fragment>
      <Modal className="share-link-modal document-center" show={show} onHide={() => closeModal()}>
        <Modal.Header>

          <Modal.Title>
            <FontAwesomeIcon className="icon-gradient me-3" icon={faPaperPlane} />
            {title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="modal-padding">
          <p className="spaced-text mb-5">
            {description}
          </p>
          {error && (
            <Alert className="alert-icon my-4" variant="danger">
              <FontAwesomeIcon icon={faExclamationCircle} />
              <div className="d-flex flex-column gap-3">
                <div className="fw-bold">{error}</div>
                {errorDetail && (
                  <div>Error Details: {errorDetail}</div>
                )}
              </div>
            </Alert>
          )}

          <Form.Group>
            <Row>
              {!email && <Col sm={7}>
                <ShareLinkEmailInput
                  disabled={isSendingInvite}
                  email={newInvite.email}
                  identifier={newInvite.id}
                  isValidEmail={isValidEmail(newInvite.email)}
                  onChange={(fieldName, newValue) => updateField(fieldName, newValue)}
                />
              </Col>}
              <Col className="mb-4" sm={email ? 12 : 5}>
                <Form.Label className="mb-1">From</Form.Label>
                <Form.Control
                  as="select"
                  disabled={isSendingInvite}
                  onChange={e => updateField('from', e.target.value)}
                >
                  {fromEmails.map(fromEmail => (
                    <option key={fromEmail.id} value={fromEmail.id}>{fromEmail.name}</option>
                  ))}
                </Form.Control>
              </Col>
            </Row>
            <div className="mt-4">
              <Form.Control
                as="textarea"
                disabled={isSendingInvite}
                placeholder="Message"
                onChange={e => updateField('message', e.target.value)}
              />
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={isSendingInvite} variant="ghost" onClick={closeModal}>
            Cancel
          </Button>
          <Button disabled={isSendingInvite || !validEmail} onClick={save}>
            {isSendingInvite ? 'Inviting...' : 'Invite'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Snackbar
        position="bottom"
        show={isSent}
        text="Email was sent"
        variant="success"
        onClose={() => setIsSent(false)}
      />
    </React.Fragment>
  );
}

const getDefaultNewInvite = (email, fromEmails) => ({ email: email || '', from: fromEmails[0].id, accessLevel: 'low' });

ShareLinkByEmailModal.propTypes = {
  additionalInviteProps: PropTypes.object,
  description: PropTypes.string.isRequired,
  email: PropTypes.string,
  fromEmails: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  hideModal: PropTypes.func.isRequired,
  leadId: PropTypes.number,
  propertyId: PropTypes.number.isRequired,
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
};
